import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { FiExternalLink, FiPlus } from 'react-icons/fi';
import { Badge, Button, Spinner } from '../../components/ui';
import ConfirmModal from '../../components/common/ConfirmModal';
import LocationFormFields from '../../components/admin/locations/LocationFormFields';
import {
  apiErrorMessage,
  emptyForm,
  formFromPage,
  inputFromForm,
  LocationForm,
} from '../../components/admin/locations/locationForm';
import { adminLocationService } from '../../services/locations';
import { propertyService } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { LocationPageAdmin } from '../../types/location';

const Page = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr);
  gap: 1.5rem;
  align-items: start;

  @media (max-width: 1000px) {
    grid-template-columns: 1fr;
  }
`;

const Sidebar = styled.aside`
  display: grid;
  gap: 0.75rem;
`;

const SidebarHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  h1 { font-size: 1.35rem; }
`;

const Item = styled.button<{ $active: boolean }>`
  display: grid;
  gap: 0.3rem;
  width: 100%;
  padding: 0.85rem 1rem;
  text-align: left;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ theme, $active }) => ($active ? theme.colors.primarySoft : '#fff')};

  strong { font-size: 0.95rem; }
  small { color: ${({ theme }) => theme.colors.text.secondary}; }
  span { display: flex; gap: 0.4rem; align-items: center; flex-wrap: wrap; }
`;

const Editor = styled.form`
  display: grid;
  gap: 1.25rem;
`;

const Toolbar = styled.div`
  position: sticky;
  top: 0;
  z-index: 5;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(6px);

  h2 { font-size: 1.1rem; }
  div { display: flex; gap: 0.5rem; flex-wrap: wrap; align-items: center; }
  a { display: inline-flex; gap: 0.35rem; align-items: center; font-weight: 600; font-size: 0.9rem; }
`;

const Empty = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const AdminLocations: React.FC = () => {
  const { showNotification } = useNotification();
  const [pages, setPages] = useState<LocationPageAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [form, setForm] = useState<LocationForm>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);

  const selected = useMemo(() => pages.find((item) => item.id === selectedId) || null, [pages, selectedId]);

  const load = useCallback(async () => {
    try {
      setPages(await adminLocationService.list());
    } catch (error) {
      showNotification(apiErrorMessage(error, 'Não foi possível carregar as páginas de local'), 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    load();
    propertyService
      .getFilterOptions()
      .then((options) => {
        const names = [...(options.neighborhoods || []), ...(options.condominiums || [])].map((item) => item.label);
        setSuggestions(Array.from(new Set(names)).sort((a, b) => a.localeCompare(b, 'pt-BR')));
      })
      .catch(() => setSuggestions([]));
  }, [load]);

  const select = (page: LocationPageAdmin | null) => {
    setSelectedId(page?.id || null);
    setForm(page ? formFromPage(page) : emptyForm());
  };

  const replace = (saved: LocationPageAdmin) => {
    setPages((current) => {
      const exists = current.some((item) => item.id === saved.id);
      return exists ? current.map((item) => (item.id === saved.id ? saved : item)) : [...current, saved];
    });
    setSelectedId(saved.id);
    setForm(formFromPage(saved));
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    try {
      const input = inputFromForm(form);
      const saved = selected
        ? await adminLocationService.update(selected.id, input)
        : await adminLocationService.create(input);
      replace(saved);
      showNotification('Página salva', 'success');
    } catch (error) {
      showNotification(apiErrorMessage(error, 'Não foi possível salvar'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const togglePublished = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const saved = await adminLocationService.setPublished(selected.id, !selected.is_published);
      replace(saved);
      showNotification(saved.is_published ? 'Página publicada' : 'Página despublicada', 'success');
    } catch (error) {
      showNotification(apiErrorMessage(error, 'Não foi possível alterar a publicação'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!selected) return;
    setConfirmDelete(false);
    try {
      await adminLocationService.remove(selected.id);
      setPages((current) => current.filter((item) => item.id !== selected.id));
      select(null);
      showNotification('Página excluída', 'success');
    } catch (error) {
      showNotification(apiErrorMessage(error, 'Não foi possível excluir'), 'error');
    }
  };

  return (
    <Page>
      <Sidebar>
        <SidebarHead>
          <h1>Páginas de local</h1>
          <Button type="button" $size="sm" onClick={() => select(null)}>
            <FiPlus /> Novo local
          </Button>
        </SidebarHead>
        {loading && <Spinner />}
        {!loading && pages.length === 0 && <Empty>Nenhuma página ainda. Crie a primeira ao lado.</Empty>}
        {pages.map((page) => (
          <Item key={page.id} type="button" $active={page.id === selectedId} onClick={() => select(page)}>
            <strong>{page.name}</strong>
            <span>
              <Badge $variant={page.is_published ? 'success' : 'neutral'}>
                {page.is_published ? 'Publicada' : 'Rascunho'}
              </Badge>
              <small>{page.property_count} imóveis · {page.path}</small>
            </span>
          </Item>
        ))}
      </Sidebar>

      <Editor onSubmit={save}>
        <Toolbar>
          <h2>{selected ? selected.name : 'Novo local'}</h2>
          <div>
            {selected?.is_published && (
              <a href={selected.path} target="_blank" rel="noopener noreferrer">
                Ver página <FiExternalLink size={14} />
              </a>
            )}
            {selected && (
              <Button type="button" $variant="ghost" onClick={() => setConfirmDelete(true)} disabled={saving}>
                Excluir
              </Button>
            )}
            {selected && (
              <Button type="button" $variant="outline" onClick={togglePublished} disabled={saving}>
                {selected.is_published ? 'Despublicar' : 'Publicar'}
              </Button>
            )}
            <Button type="submit" disabled={saving || !form.name.trim()}>
              {saving ? 'Salvando…' : 'Salvar'}
            </Button>
          </div>
        </Toolbar>
        <LocationFormFields form={form} onChange={setForm} suggestions={suggestions} />
      </Editor>

      <ConfirmModal
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={remove}
        title="Excluir página"
        message={`Excluir a página "${selected?.name || ''}"? Os imóveis não são afetados.`}
        confirmText="Excluir"
        type="danger"
      />
    </Page>
  );
};

export default AdminLocations;
